/**
 * Utility functions for the Moby application
 */

/**
 * Format bytes to a human-readable format
 * @param bytes Number of bytes to format
 * @param decimals Number of decimal places to show
 * @returns Formatted string (e.g. "1.23 MB")
 */
export function formatBytes(bytes: number, decimals: number = 2): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
}

/**
 * Format milliseconds into a human-readable time remaining string
 * @param milliseconds Number of milliseconds remaining
 * @returns Formatted time remaining string (e.g. "2 hours 3 minutes remaining")
 */
export function formatTimeRemaining(milliseconds: number): string {
  if (milliseconds < 0) return 'Unknown time remaining';
  if (milliseconds === 0) return 'Complete';
  
  const seconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  
  if (days > 0) {
    return `${days} day${days !== 1 ? 's' : ''} ${hours % 24} hour${hours % 24 !== 1 ? 's' : ''} remaining`;
  } else if (hours > 0) {
    return `${hours} hour${hours !== 1 ? 's' : ''} ${minutes % 60} minute${minutes % 60 !== 1 ? 's' : ''} remaining`;
  } else if (minutes > 0) {
    return `${minutes} minute${minutes !== 1 ? 's' : ''} ${seconds % 60} second${seconds % 60 !== 1 ? 's' : ''} remaining`;
  } else {
    return `${seconds} second${seconds !== 1 ? 's' : ''} remaining`;
  }
}

/**
 * Format a timestamp into a human-readable date/time
 * @param timestamp Unix timestamp in milliseconds
 * @returns Formatted date string (e.g. "October 28, 2024 10:45 AM")
 */
export function formatTimestamp(timestamp: number): string {
  return new Date(timestamp).toLocaleString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
}

/**
 * Calculate transfer rate in bytes per second
 * @param bytesTransferred Total bytes transferred
 * @param startTimeMs Start time in milliseconds
 * @param currentTimeMs Current time in milliseconds 
 * @returns Transfer rate in bytes per second
 */
export function calculateTransferRate(
  bytesTransferred: number,
  startTimeMs: number,
  currentTimeMs: number
): number {
  const elapsedSeconds = (currentTimeMs - startTimeMs) / 1000;
  if (elapsedSeconds <= 0) return 0;
  return bytesTransferred / elapsedSeconds;
}

/**
 * Estimate time remaining for a transfer
 * @param bytesTransferred Bytes transferred so far
 * @param totalBytes Total bytes to transfer
 * @param startTimeMs Start time in milliseconds
 * @param currentTimeMs Current time in milliseconds
 * @returns Estimated milliseconds remaining
 */
export function estimateTimeRemaining(
  bytesTransferred: number,
  totalBytes: number,
  startTimeMs: number,
  currentTimeMs: number
): number {
  if (bytesTransferred <= 0) return 0;
  
  const elapsedMs = currentTimeMs - startTimeMs;
  const bytesRemaining = totalBytes - bytesTransferred;
  const msPerByte = elapsedMs / bytesTransferred;
  
  return bytesRemaining * msPerByte;
}
